import { Button } from "primereact/button";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../../shared/Fetch";
import { JVInput } from "../../../shared/JVInput";
import { JVCheckbox } from "../../../shared/JVCheckbox";
import { JVDropdown } from "../../../shared/JVDropdown";
import { JVDate } from "../../../shared/JVDate";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { format } from "date-fns";

const schema = yup.object({
    roomTypeId: yup.number().required().min(1),
    mealPlan: yup.string().required("Meal plan is required"),
    startDate: yup.date().typeError('Start date is required').required('Start date is required'),
    endDate: yup.date().typeError('End date is required').required('End date is required'),
    extraAdultPrice: yup.number().typeError('Extra adult price is required').required('Extra adult price is required').min(0, 'Extra adult price is required'),
    extraChildPrice: yup.number().typeError('Extra child price is required').required('Extra child price is required').min(0, 'Extra child price is required'),
    minimumStay: yup.number().typeError('Minimum stay is required').required('Minimum stay is required').min(1, 'Minimum stay is required').max(30, 'Maximum 30 Days')
});

interface Iprops {
    roomTypeId: number,
    capacity: number,
    onSuccess: () => void,
    onClose: () => void,
    toast: any
}

interface RoomTypeCalenderDto {
    roomTypeId: number
    mealPlan: 'AP' | 'CP' | 'MAP' | 'EP'
    startDate: Date | string
    endDate: Date | string
    monday: boolean
    tuesday: boolean
    wednesday: boolean
    thursday: boolean
    friday: boolean
    saturday: boolean
    sunday: boolean
    roomTypePrices: RoomTypePriceDto[]
    extraAdultPrice: number
    extraChildPrice: number
    minimumStay: number
}
interface RoomTypePriceDto {
    sharing: number
    price?: number
}

export const RoomPriceForm = (props: Iprops) => {
    const [submitting, setSubmitting] = useState(false);
    const [dateType, setDateType] = useState<'Range' | 'Individual'>('Range');
    const [weeks, setWeeks] = useState<'Weekdays' | 'Weekend' | 'Custom'>('Custom');
    const [rtPrise, setRtPrise] = useState<RoomTypePriceDto[]>([
        { sharing: 1 }
    ]);

    const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<RoomTypeCalenderDto>({
        resolver: yupResolver(schema),
        defaultValues: {
            mealPlan: 'EP',
            sunday: true,
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true
        },
        shouldFocusError: false,
    });

    useEffect(() => {
        const rtp: RoomTypePriceDto[] = [];
        for (let i = 1; i <= props.capacity; i++) {
            rtp.push({ sharing: i });
        }
        setRtPrise(rtp);
    }, [props.capacity])

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
    const isWeekend = (d: typeof days[number]) => ['saturday', 'sunday'].includes(d);

    useEffect(() => {
        days.forEach(day => {
            if (weeks === 'Custom') setValue(day, true);
            else if (weeks === 'Weekdays') setValue(day, !isWeekend(day));
            else if (weeks === 'Weekend') setValue(day, isWeekend(day));
        });
    }, [weeks, setValue]);

    useEffect(() => {
        if (props.roomTypeId) setValue('roomTypeId', props.roomTypeId);
    }, [props.roomTypeId])

    const startDate = watch('startDate');

    useEffect(() => {
        if (dateType === 'Individual') {
            setValue('endDate', startDate);
            days.forEach(day => setValue(day, true));
        } else {
            days.forEach(day => {
                if (weeks === 'Custom') setValue(day, true);
                else if (weeks === 'Weekdays') setValue(day, !isWeekend(day));
                else if (weeks === 'Weekend') setValue(day, isWeekend(day));
            });
        }
    }, [dateType, startDate])

    const saveToDB = async (body: RoomTypeCalenderDto) => {
        setSubmitting(true);
        const response = await Fetch(`RoomTypePrice`, 'POST', body, null, props.toast);
        if (response) {
            props.toast.current.show({ severity: 'success', detail: 'Updated Successfully', life: 3000 });
            props.onSuccess();
        }
        setSubmitting(false);
    }

    function onSubmit(data: RoomTypeCalenderDto) {
        if (rtPrise.some(f => f.price === undefined || f.price < 0)) {
            props.toast.current.show({ severity: 'error', detail: 'Fill all occupancy prices with positive value', life: 3000 });
            return;
        }

        const days = [
            data.monday,
            data.tuesday,
            data.wednesday,
            data.thursday,
            data.friday,
            data.saturday,
            data.sunday,
        ];
        if (!days.some(Boolean)) {
            props.toast.current.show({ severity: 'error', detail: 'Please select at least one day', life: 3000 });
            return;
        }

        data.roomTypePrices = rtPrise.map(rp => ({ sharing: rp.sharing, price: rp.price }));
        data.startDate = format(new Date(data.startDate), "yyyy-MM-dd");
        data.endDate = format(new Date(data.endDate), "yyyy-MM-dd");
        saveToDB(data);
    }

    const dt = ['Range', 'Individual'] as const;
    const wk = ['Weekdays', 'Weekend', 'Custom'] as const;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
            <div className="field col-12">
                <div className="mb-2">Date Type</div>
                {dt.map(a => {
                    const selected = dateType === a;
                    return <Tag key={a} className="mr-2 cursor-pointer"
                        icon={selected ? 'pi pi-check-circle' : undefined}
                        style={{ backgroundColor: selected ? '#22c55e' : '#64748b' }}
                        value={a} onClick={() => setDateType(a)} />
                })}
            </div>
            <JVDropdown required label="MealPlan" column={1} control={control} optionLabel="id" optionValue="id" options={[{ id: 'EP' }, { id: 'AP' }, { id: 'CP' }, { id: 'MAP' }]} name="mealPlan" error={errors.mealPlan?.message} />
            <JVDate required control={control} label={dateType === 'Individual' ? 'Date' : "From Date"} name="startDate" error={errors.startDate?.message} />
            {dateType === 'Range' &&
                <>
                    <JVDate minDate={new Date(startDate)} required control={control} label="To Date" name="endDate" error={errors.endDate?.message} />
                    <div className="field col-12">
                        {wk.map(w => {
                            const selected = weeks === w;
                            return <Tag key={w} className="mr-2 cursor-pointer"
                                icon={selected ? 'pi pi-check-circle' : undefined}
                                style={{ backgroundColor: selected ? '#22c55e' : '#64748b' }}
                                value={w} onClick={() => setWeeks(w)} />
                        })}
                    </div>
                    <JVCheckbox column={4} label="Sunday" control={control} name="sunday" />
                    <JVCheckbox column={4} label="Monday" control={control} name="monday" />
                    <JVCheckbox column={4} label="Tuesday" control={control} name="tuesday" />
                    <JVCheckbox column={4} label="Wednesday" control={control} name="wednesday" />
                    <JVCheckbox column={4} label="Thursday" control={control} name="thursday" />
                    <JVCheckbox column={4} label="Friday" control={control} name="friday" />
                    <JVCheckbox column={4} label="Saturday" control={control} name="saturday" />
                </>
            }
            <div className="field col-12">
                {rtPrise.filter(f => f.sharing === props.capacity).map(rp => {
                    return <><InputText className="mt-2" key={rp.sharing} placeholder={`BAR Price`} type="number" value={rp.price} onChange={(e) => {
                        setRtPrise(prev => {
                            const str = e.target.value;
                            const val = +str;
                            return prev.map(p => p.sharing === rp.sharing ? { ...p, price: (str.length === 0 && val === 0) ? undefined : val } : p)
                        })
                    }} />
                        {/* {(rp.price === undefined || rp.price < 0) && <small className="p-error" >Requied*</small>} */}
                    </>
                })}
                {rtPrise.filter(f => f.sharing !== props.capacity).map(rp => {
                    return <><InputText className="mt-2" key={rp.sharing} placeholder={`${rp.sharing} Occupancy Price`} type="number" value={rp.price} onChange={(e) => {
                        setRtPrise(prev => {
                            const str = e.target.value;
                            const val = +str;
                            return prev.map(p => p.sharing === rp.sharing ? { ...p, price: (str.length === 0 && val === 0) ? undefined : val } : p)
                        })
                    }} />
                        {/* {(rp.price === undefined || rp.price < 0) && <small className="p-error" >Requied*</small>} */}
                    </>
                })}
            </div>
            <JVInput required column={3} type="number" label="Minimum Stay (Max 30 Days)" control={control} name="minimumStay" error={errors.minimumStay?.message} />
            <JVInput required column={3} type="number" label="ExtraAdultPrice" control={control} name="extraAdultPrice" error={errors.extraAdultPrice?.message} />
            <JVInput required column={3} type="number" label="ExtraChildPrice" control={control} name="extraChildPrice" error={errors.extraChildPrice?.message} />
            <div className="col-12 md:col-12 flex gap-4 justify-content-end mt-2">
                <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                <Button style={{ width: '100px' }} label="CLOSE" onClick={props.onClose} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
            </div>
        </form>
    );
}